import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  FileText,
  Target,
  User,
  Calendar,
  Globe,
  Send,
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  Pencil,
  Save,
  X,
  Eye,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { articlesApi } from "@/services/api";
import type { ArticleDetail, ArticleUpdate } from "@/types";

interface ArticleViewProps {
  articleId: string;
  onBack: () => void;
  onPublish?: () => void;
  onReoptimize?: () => void;
  isReoptimizing?: boolean;
}

export function ArticleView({
  articleId,
  onBack,
  onPublish,
  onReoptimize,
  isReoptimizing,
}: ArticleViewProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  // Edit form state
  const [editedTitle, setEditedTitle] = useState("");
  const [editedMetaTitle, setEditedMetaTitle] = useState("");
  const [editedMetaDescription, setEditedMetaDescription] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedHtmlContent, setEditedHtmlContent] = useState("");
  const [editedTags, setEditedTags] = useState("");

  // Image regeneration state
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [regenStartedAt, setRegenStartedAt] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const {
    data: article,
    isLoading,
    error,
  } = useQuery<ArticleDetail>({
    queryKey: ["article", articleId],
    queryFn: () => articlesApi.get(articleId),
  });

  // Initialize edit form when article loads
  useEffect(() => {
    if (article) {
      setEditedTitle(article.title);
      setEditedMetaTitle(article.meta_title || "");
      setEditedMetaDescription(article.meta_description || "");
      setEditedContent(article.html_content || article.content);
      setEditedHtmlContent(article.html_content || "");
      setEditedTags(article.tags?.join(", ") || "");
    }
  }, [article]);

  const updateMutation = useMutation({
    mutationFn: (data: ArticleUpdate) => articlesApi.update(articleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["article", articleId] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      setIsEditing(false);
    },
  });

  const regenerateImageMutation = useMutation({
    mutationFn: (instructions: string) =>
      articlesApi.regenerateImage(articleId, {
        image_instructions: instructions || undefined,
      }),
    onSuccess: () => {
      setRegenStartedAt(Date.now());
      // Poll the article every 4s for up to ~60s so the new image_url shows up
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        await queryClient.invalidateQueries({
          queryKey: ["article", articleId],
        });
        if (attempts < 15) setTimeout(poll, 4000);
        else setRegenStartedAt(null);
      };
      setTimeout(poll, 4000);
    },
  });

  // Stop polling once the image_url changes (regen finished)
  const imageUrlForEffect = article?.image_url;
  useEffect(() => {
    if (regenStartedAt && imageUrlForEffect) {
      setRegenStartedAt(null);
      setRegenOpen(false);
      setRegenInstructions("");
    }
    // Intentionally keying off image_url only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrlForEffect]);

  const handleCopyContent = async () => {
    if (article?.content) {
      await navigator.clipboard.writeText(article.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setIsPreview(false);
  };

  const handleCancelEdit = () => {
    if (article) {
      setEditedTitle(article.title);
      setEditedMetaTitle(article.meta_title || "");
      setEditedMetaDescription(article.meta_description || "");
      setEditedContent(article.html_content || article.content);
      setEditedHtmlContent(article.html_content || "");
      setEditedTags(article.tags?.join(", ") || "");
    }
    setIsEditing(false);
    setIsPreview(false);
  };

  const handleSave = () => {
    const tags = editedTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    updateMutation.mutate({
      title: editedTitle,
      meta_title: editedMetaTitle || undefined,
      meta_description: editedMetaDescription || undefined,
      // content = plain text; html_content = rendered HTML used for publishing.
      // Both must be written so added links + formatting survive save + WP publish.
      content: editedContent || undefined,
      html_content: editedHtmlContent || undefined,
      tags: tags.length > 0 ? tags : undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="me-2 h-4 w-4" />
          {t("articleView.backToArticles")}
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("articleView.loadingArticle")}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="me-2 h-4 w-4" />
          {t("articleView.backToArticles")}
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-destructive">
            {t("articleView.failedToLoad")}
          </CardContent>
        </Card>
      </div>
    );
  }

  const wordCount = (editedHtmlContent || editedContent || "")
    .replace(/<[^>]*>/g, "")
    .split(/\s+/)
    .filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="me-2 h-4 w-4" />
          {t("articleView.backToArticles")}
        </Button>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => setIsPreview(!isPreview)}
              >
                <Eye className="me-2 h-4 w-4" />
                {isPreview ? t("app.edit") : t("app.preview")}
              </Button>
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="me-2 h-4 w-4" />
                {t("app.cancel")}
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                <Save className="me-2 h-4 w-4" />
                {updateMutation.isPending
                  ? t("articleView.saving")
                  : t("app.save")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleStartEdit}>
                <Pencil className="me-2 h-4 w-4" />
                {t("app.edit")}
              </Button>
              <Button variant="outline" onClick={handleCopyContent}>
                {copied ? (
                  <>
                    <Check className="me-2 h-4 w-4" />
                    {t("app.copied")}
                  </>
                ) : (
                  <>
                    <Copy className="me-2 h-4 w-4" />
                    {t("articleView.copyContent")}
                  </>
                )}
              </Button>
              {article.status === "ready" && onPublish && (
                <Button onClick={onPublish}>
                  <Send className="me-2 h-4 w-4" />
                  {t("articleView.publishToWordPress")}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const resp =
                      await articlesApi.getGenerationConfig(articleId);
                    const config = resp.generation_config || {};
                    const params = new URLSearchParams();
                    params.set("duplicate", JSON.stringify(config));
                    window.location.href = `/articles?${params.toString()}`;
                  } catch {
                    window.location.href = `/articles?duplicate={}`;
                  }
                }}
              >
                <Copy className="me-2 h-4 w-4" />
                Duplicate & Edit
              </Button>
              {article.status === "ready" && onReoptimize && (
                <Button
                  variant="outline"
                  onClick={onReoptimize}
                  disabled={isReoptimizing}
                >
                  {isReoptimizing ? (
                    <>
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                      {t("articleView.reoptimizing")}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="me-2 h-4 w-4" />
                      {t("articleView.reoptimize")}
                    </>
                  )}
                </Button>
              )}
              {article.wp_post_url && (
                <Button variant="outline" asChild>
                  <a
                    href={article.wp_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="me-2 h-4 w-4" />
                    {t("articleView.viewOnSite")}
                  </a>
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Error display */}
      {updateMutation.isError && (
        <Card className="border-destructive">
          <CardContent className="py-4 text-destructive">
            {t("articleView.failedToSave")}
          </CardContent>
        </Card>
      )}

      {/* Article Header */}
      <Card>
        <CardHeader>
          {isEditing && !isPreview ? (
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-xl font-bold"
                placeholder="Article title"
              />
            </div>
          ) : (
            <CardTitle className="text-2xl">
              {isEditing ? editedTitle : article.title}
            </CardTitle>
          )}
          <CardDescription className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(article.created_at).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {(isEditing
                ? wordCount
                : article.word_count
              )?.toLocaleString()}{" "}
              words
            </span>
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Metrics - only show when not editing */}
      {!isEditing && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Target className="h-4 w-4" />
                <span className="text-sm">{t("articleView.seoScore")}</span>
              </div>
              <div className="text-2xl font-bold">
                {article.seo_score !== null
                  ? `${article.seo_score}/100`
                  : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Globe className="h-4 w-4" />
                <span className="text-sm">{t("articleView.geoScore")}</span>
              </div>
              <div className="text-2xl font-bold">
                {article.geo_score !== null
                  ? `${article.geo_score}/100`
                  : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <User className="h-4 w-4" />
                <span className="text-sm">{t("articleView.humanScore")}</span>
              </div>
              <div className="text-2xl font-bold">
                {article.human_score !== null
                  ? `${article.human_score}%`
                  : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <FileText className="h-4 w-4" />
                <span className="text-sm">{t("articles.status.draft")}</span>
              </div>
              <div className="text-2xl font-bold capitalize">
                {article.status}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Send className="h-4 w-4" />
                <span className="text-sm">
                  {t("articles.status.published")}
                </span>
              </div>
              <div className="text-2xl font-bold">
                {article.wp_post_id ? t("app.yes") : t("app.no")}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SEO Meta */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("articleView.seoMeta")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing && !isPreview ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="metaTitle">{t("articleView.metaTitle")}</Label>
                <Input
                  id="metaTitle"
                  value={editedMetaTitle}
                  onChange={(e) => setEditedMetaTitle(e.target.value)}
                  placeholder={t("articleView.metaTitlePlaceholder")}
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground">
                  {t("articleView.characters", {
                    count: editedMetaTitle.length,
                    max: 60,
                  })}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="metaDescription">
                  {t("articleView.metaDescription")}
                </Label>
                <Textarea
                  id="metaDescription"
                  value={editedMetaDescription}
                  onChange={(e) => setEditedMetaDescription(e.target.value)}
                  placeholder={t("articleView.metaDescriptionPlaceholder")}
                  maxLength={160}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {t("articleView.characters", {
                    count: editedMetaDescription.length,
                    max: 160,
                  })}
                </p>
              </div>
            </>
          ) : (
            <>
              {(isEditing ? editedMetaTitle : article.meta_title) && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    {t("articleView.metaTitle")}
                  </div>
                  <div className="font-medium">
                    {isEditing ? editedMetaTitle : article.meta_title}
                  </div>
                </div>
              )}
              {(isEditing
                ? editedMetaDescription
                : article.meta_description) && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    {t("articleView.metaDescription")}
                  </div>
                  <div>
                    {isEditing
                      ? editedMetaDescription
                      : article.meta_description}
                  </div>
                </div>
              )}
              {article.slug && !isEditing && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    {t("articleView.slug")}
                  </div>
                  <code className="text-sm bg-secondary px-2 py-1 rounded">
                    {article.slug}
                  </code>
                </div>
              )}
              {!article.meta_title &&
                !article.meta_description &&
                !isEditing && (
                  <p className="text-muted-foreground">
                    {t("articleView.noSeoMeta")}
                  </p>
                )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Keywords/Tags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("articleView.keywords")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing && !isPreview ? (
            <div className="space-y-2">
              <Input
                value={editedTags}
                onChange={(e) => setEditedTags(e.target.value)}
                placeholder={t("articleView.keywordsPlaceholder")}
              />
              <p className="text-xs text-muted-foreground">
                {t("articleView.separateWithCommas")}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(isEditing
                ? editedTags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter(Boolean)
                : article.tags || []
              ).map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
              {(
                (isEditing ? editedTags : article.tags?.join(",")) || ""
              ).trim() === "" && (
                <p className="text-muted-foreground">
                  {t("articleView.noKeywords")}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Article Content */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("articleView.content")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing && !isPreview ? (
            <RichTextEditor
              content={editedContent}
              onHtmlChange={(html) => {
                setEditedHtmlContent(html);
                setEditedContent(html);
              }}
              placeholder={t("articleView.contentPlaceholder")}
            />
          ) : (
            <>
              {(isEditing ? editedHtmlContent : article.html_content) ? (
                <div
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      isEditing ? editedHtmlContent : article.html_content,
                    ),
                  }}
                />
              ) : (
                <div className="whitespace-pre-wrap font-mono text-sm bg-secondary/50 p-4 rounded-lg max-h-[600px] overflow-y-auto">
                  {isEditing ? editedContent : article.content}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Featured Image */}
      {article.image_url && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-lg">
              {t("articleView.featuredImage")}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRegenOpen((v) => !v)}
              disabled={
                regenerateImageMutation.isPending || regenStartedAt !== null
              }
            >
              {regenerateImageMutation.isPending || regenStartedAt !== null ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Regenerating…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Regenerate image
                </>
              )}
            </Button>
          </CardHeader>
          <CardContent>
            {regenOpen && (
              <div className="mb-4 rounded-md border bg-muted/30 p-3 space-y-2">
                <Label htmlFor="regenInstructions" className="text-sm">
                  Optional instructions (short description of what you want)
                </Label>
                <Textarea
                  id="regenInstructions"
                  value={regenInstructions}
                  onChange={(e) => setRegenInstructions(e.target.value)}
                  placeholder="e.g. close-up of a steaming mug of tea beside fresh mint leaves, warm morning light"
                  rows={2}
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      regenerateImageMutation.mutate(regenInstructions)
                    }
                    disabled={regenerateImageMutation.isPending}
                  >
                    Generate new image
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRegenOpen(false);
                      setRegenInstructions("");
                    }}
                  >
                    Cancel
                  </Button>
                  {regenStartedAt && (
                    <span className="text-xs text-muted-foreground">
                      Running — usually ~15s. The image below will refresh
                      automatically.
                    </span>
                  )}
                </div>
              </div>
            )}
            <img
              src={article.image_url}
              alt={article.title}
              className="rounded-lg max-w-full h-auto"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
